import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { HERO } from "../../data/content";
import { MaskedLines, EASE } from "./Reveal";

// Hero copy reveals AFTER the background node system has settled (~1.5s),
// so the load reads as: nodes arrive → system settles → text appears.
const NODE_SETTLE_MS = 900;

export function Hero() {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) {
      setReady(true);
      return;
    }
    const t = setTimeout(() => setReady(true), NODE_SETTLE_MS);
    // if the page loaded in a background tab (timers throttled), reveal on focus
    const onVis = () => {
      if (!document.hidden) setReady(true);
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      clearTimeout(t);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);

  return (
    <section id="top" className="relative flex min-h-[100svh] w-full items-center pt-32 md:pt-28">
      <div className="relative z-10 mx-auto w-full max-w-[1440px] px-6 md:px-12 lg:px-24">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={ready ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
          transition={{ duration: 0.6, ease: EASE }}
          className="flex items-center gap-3"
        >
          <span className="h-[6px] w-[6px] rounded-full bg-brand" />
          <span className="eyebrow">{HERO.eyebrow}</span>
        </motion.div>

        <h1 className="mt-8 max-w-[18ch] font-display font-light leading-[1.04] tracking-[-0.02em] text-content text-[2.7rem] sm:text-5xl md:text-6xl lg:text-[4.4rem] xl:text-[5.1rem]">
          <MaskedLines lines={[HERO.missionParts]} stagger={0.1} delay={0.12} trigger="signal" active={ready} />
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={ready ? { opacity: 1, y: 0 } : { opacity: 0, y: 12 }}
          transition={{ duration: 0.8, ease: EASE, delay: 0.5 }}
          className="mt-12 inline-flex items-center gap-2 rounded-full border border-line px-3 py-1.5"
        >
          <span className="relative flex h-1.5 w-1.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-brand opacity-70" />
            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-brand" />
          </span>
          <span className="font-mono-c text-[0.62rem] uppercase tracking-[0.14em] text-muted-c">{HERO.badge}</span>
        </motion.div>
      </div>
    </section>
  );
}
